import { useCallback, useEffect, useMemo, useState } from "react";
import OrderDetailsWindow from "../../../OrderDetails/OrderDetailsWindow";
import { useOrders } from "../../../../contexts/OrderContext";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { runOrderPreparationPipeline, isPipelineRunning } from "../../../../lib/corel/pipeline/orderPreparation";
import { clearPreparedPipeline } from "../../../../lib/corel/pipeline/pipelineKeys";
import type { QuotationSaveNowSession } from "../types";

interface QuotationOrderDetailsLauncherProps {
	session: QuotationSaveNowSession;
}

type PipelineStatus = "idle" | "running" | "ok" | "error";
type PrepStep =
	| "organize"
	| "distribute"
	| "monitor-start"
	| "monitor-sync"
	| "done";

function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export function QuotationOrderDetailsLauncher({
	session,
}: QuotationOrderDetailsLauncherProps) {
	const { selectPedido, selectedPedido } = useOrders();
	const [ready, setReady] = useState(false);
	const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus>("idle");
	const [pipelineError, setPipelineError] = useState<string | null>(null);
	const [pipelineNonce, setPipelineNonce] = useState(0);
	const [prepStep, setPrepStep] = useState<PrepStep>("organize");
	const [showDetails, setShowDetails] = useState(false);
	const [pendingOpenDetails, setPendingOpenDetails] = useState(false);
	const [snapshotReady, setSnapshotReady] = useState(false);

	const sessionPedidoId = Number(session.pedidoId);
	const sessionItemId = String(session.itemId);

	useEffect(() => {
		sendToTerminal("🚀 [QUOTATION_SAVE] Iniciando launcher de salvamento", {
			pedidoId: sessionPedidoId,
			itemId: sessionItemId,
		});
		selectPedido(session.pedido);
	}, [selectPedido, session.pedido, sessionItemId, sessionPedidoId]);

	useEffect(() => {
		if (selectedPedido && Number(selectedPedido.orc_id) === sessionPedidoId) {
			setReady(true);
			sendToTerminal("✅ [QUOTATION_SAVE] Pedido carregado no contexto", {
				pedidoId: sessionPedidoId,
				itemId: sessionItemId,
				itensCount: selectedPedido.itens?.length ?? 0,
			});
		}
	}, [selectedPedido, sessionItemId, sessionPedidoId]);

	useEffect(() => {
		if (pendingOpenDetails) {
			sendToTerminal("🔍 [QUOTATION_SAVE] Aguardando contexto para abrir detalhes", {
				pendingOpenDetails,
				hasSelectedPedido: !!selectedPedido,
				selectedPedidoId: selectedPedido?.orc_id,
				itensLength: selectedPedido?.itens?.length,
			});
		}

		if (pendingOpenDetails && selectedPedido) {
			if (!selectedPedido.itens || selectedPedido.itens.length === 0) {
				sendToTerminal("⚠️ [QUOTATION_SAVE] Pedido sem itens - bloqueando abertura", {
					pedidoId: selectedPedido?.orc_id,
				});
				return;
			}

			sendToTerminal("✅ [QUOTATION_SAVE] Contexto validado, abrindo OrderDetailsWindow", {
				pedidoId: selectedPedido.orc_id,
				itemId: sessionItemId,
				firstItemId: selectedPedido.itens[0]?.oit_id,
			});

			setTimeout(() => {
				setShowDetails(true);
				setPendingOpenDetails(false);
			}, 50);
		}
	}, [pendingOpenDetails, selectedPedido, sessionItemId]);

	useEffect(() => {
		if (!ready || !selectedPedido) return;

		if (!selectedPedido.itens || selectedPedido.itens.length === 0) {
			sendToTerminal("⚠️ [QUOTATION_SAVE] Pedido invalidado antes da pipeline", {
				pedidoId: selectedPedido?.orc_id,
			});
			setPipelineStatus("error");
			setPipelineError("Pedido não possui itens válidos para abrir o fluxo de salvamento.");
			return;
		}

		const pedidoId = selectedPedido.orc_id;
		const itemId = selectedPedido.itens.find(
			(item) => String(item.oit_id) === sessionItemId,
		)?.oit_id ?? selectedPedido.itens[0]?.oit_id;

		let cancelled = false;
		const safetyTimeout = setTimeout(() => {
			setPipelineStatus((currentStatus) => {
				if (currentStatus === "running") {
					sendToTerminal("⚠️ [QUOTATION_SAVE] Timeout 15s - forçando abertura", {
						pedidoId,
						itemId,
					});
					setPendingOpenDetails(true);
					return "ok";
				}

				return currentStatus;
			});
		}, 15000);

		(async () => {
			sendToTerminal("📦 [QUOTATION_SAVE] Iniciando preparação via pipeline centralizada", {
				pedidoId,
				itemId,
			});

			try {
				clearPreparedPipeline(pedidoId, itemId);
				sessionStorage.removeItem(`corel:dist:v1:${pedidoId}:${itemId}`);
			} catch {
				// Cleanup best effort.
			}

			setPipelineStatus("running");
			setPipelineError(null);
			setPrepStep("organize");
			setSnapshotReady(false);
			setShowDetails(false);

			try {
				const result = await runOrderPreparationPipeline({
					pedidoId,
					itemId,
					scope: "all",
					onProgress: (evt) => {
						try {
							const stage = evt?.stage || "";
							if (
								stage === "unified:start" ||
								stage === "unified:sending" ||
								stage.startsWith("organize")
							) {
								setPrepStep("organize");
							} else if (stage.startsWith("distribute")) {
								setPrepStep("distribute");
							} else if (
								stage === "unified:waiting" ||
								stage === "unified:processing" ||
								stage === "unified:persisting" ||
								stage.startsWith("monitor:start") ||
								stage === "monitor:ack"
							) {
								setPrepStep("monitor-start");
							} else if (stage.startsWith("monitor:status") || stage.startsWith("monitor:sync")) {
								setPrepStep("monitor-sync");
							} else if (
								stage === "unified:success" ||
								stage === "snapshot:persisted" ||
								stage === "end"
							) {
								setPrepStep("done");
							}
						} catch {
							// progress mapping best effort
						}
					},
				});

				if (cancelled) return;

				if (result.status === "cancelled" && result.error?.code === "already_running") {
					sendToTerminal("⏭️ [QUOTATION_SAVE] Pipeline já em execução, aguardando conclusão", {
						pedidoId,
						itemId,
					});

					const maxWait = 20000;
					const checkInterval = 300;
					let waited = 0;

					while (waited < maxWait && !cancelled) {
						await delay(checkInterval);
						waited += checkInterval;

						if (!isPipelineRunning()) {
							sendToTerminal("✅ [QUOTATION_SAVE] Pipeline anterior concluiu", {
								pedidoId,
								itemId,
								waited,
							});
							setPrepStep("done");
							setPipelineStatus("ok");
							setPendingOpenDetails(true);
							return;
						}
					}

					if (!isPipelineRunning()) {
						setPrepStep("done");
						setPipelineStatus("ok");
						setPendingOpenDetails(true);
						return;
					}

					setPipelineStatus("error");
					setPipelineError(
						"Uma preparação anterior não concluiu a tempo. Tente novamente.",
					);
					return;
				}

				if (result.status !== "success") {
					const errorMsg = result.error?.message || "Falha ao preparar documento";
					sendToTerminal("💥 [QUOTATION_SAVE] Pipeline falhou", {
						pedidoId,
						itemId,
						error: errorMsg,
						code: result.error?.code,
					});
					setPipelineStatus("error");
					setPipelineError(errorMsg);
					return;
				}

				setPrepStep("done");
				setPipelineStatus("ok");
				sendToTerminal("✅ [QUOTATION_SAVE] Pipeline concluída com sucesso", {
					pedidoId,
					itemId,
					pipelineId: result.pipelineId,
					docKey: result.docKey,
				});
				setPendingOpenDetails(true);
			} catch (error) {
				if (cancelled) return;
				const errorMsg =
					error instanceof Error ? error.message : "Falha na preparação do documento";
				sendToTerminal("💥 [QUOTATION_SAVE] Exceção na pipeline", {
					pedidoId,
					itemId,
					error: errorMsg,
				});
				setPipelineStatus("error");
				setPipelineError(errorMsg);

				try {
					clearPreparedPipeline(pedidoId, itemId);
					sessionStorage.removeItem(`corel:dist:v1:${pedidoId}:${itemId}`);
				} catch {
					// Cleanup best effort.
				}
			} finally {
				clearTimeout(safetyTimeout);
			}
		})();

		return () => {
			cancelled = true;
			clearTimeout(safetyTimeout);
		};
	}, [ready, selectedPedido?.orc_id, pipelineNonce, sessionItemId]);

	const handleClose = useCallback(() => {
		sendToTerminal("🚪 [QUOTATION_SAVE] Fechando launcher de salvamento", {
			pedidoId: selectedPedido?.orc_id ?? sessionPedidoId,
			itemId: sessionItemId,
		});

		try {
			const pid = selectedPedido?.orc_id ?? sessionPedidoId;
			const iid =
				selectedPedido?.itens?.find((item) => String(item.oit_id) === sessionItemId)
					?.oit_id ??
				Number(sessionItemId);
			if (pid && iid) {
				clearPreparedPipeline(pid, iid);
				sessionStorage.removeItem(`corel:dist:v1:${pid}:${iid}`);
			}
		} catch {
			// Cleanup best effort.
		}

		setShowDetails(false);
		setSnapshotReady(false);
		setPendingOpenDetails(false);
		session.onClose();
	}, [selectedPedido, session, sessionItemId, sessionPedidoId]);

	const handleSnapshotReady = useCallback(() => {
		sendToTerminal("✅ [QUOTATION_SAVE] Snapshot aplicado no OrderDetailsWindow", {
			pedidoId: sessionPedidoId,
			itemId: sessionItemId,
		});
		setSnapshotReady(true);
	}, [sessionItemId, sessionPedidoId]);

	const orderDetailsKey = useMemo(
		() => `quotation-save-${sessionPedidoId}-${sessionItemId}`,
		[sessionItemId, sessionPedidoId],
	);

	return (
		<>
			{showDetails && selectedPedido && (
				<OrderDetailsWindow
					key={orderDetailsKey}
					onClose={handleClose}
					onNavigateToMain={handleClose}
					pedidoId={String(selectedPedido?.orc_id || "")}
					itemId={sessionItemId}
					initialDelivery={session.initialDelivery}
					hydrateFromPreparedSnapshot={true}
					hideInternalLoading={!snapshotReady}
					onSnapshotReady={handleSnapshotReady}
				/>
			)}

			{(pipelineStatus !== "ok" || !showDetails || (showDetails && !snapshotReady)) && (
				<div className="fixed inset-0 bg-navy-900/70 backdrop-blur-[1px] flex items-center justify-center z-[60]">
					<div className="bg-navy-800 border border-navy-700 rounded p-4 w-full max-w-sm text-gray-200">
						{pipelineStatus === "error" ? (
							<>
								<p className="text-sm font-semibold text-red-300">
									Erro ao preparar documento
								</p>
								<p className="text-xs mt-2 text-gray-300">
									{pipelineError || "Falha inesperada na preparação do salvamento."}
								</p>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
									<button
										onClick={() => {
											setPipelineError(null);
											setPipelineStatus("idle");
											setPendingOpenDetails(false);
											setShowDetails(false);
											setSnapshotReady(false);
											setPipelineNonce((value) => value + 1);
										}}
										className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-500"
									>
										Tentar novamente
									</button>
									<button
										onClick={handleClose}
										className="px-3 py-2 text-xs bg-gray-600 text-white rounded hover:bg-gray-500"
									>
										Fechar
									</button>
								</div>
							</>
						) : (
							<>
								<p className="text-sm font-semibold">
									{pipelineStatus === "ok" && showDetails && !snapshotReady
										? "Carregando objetos..."
										: pipelineStatus === "ok" && !showDetails
											? "Validando ambiente..."
											: "Preparando documento no CorelDRAW"}
								</p>
								<p className="text-xs mt-1 text-gray-400">
									Este processo leva apenas alguns instantes.
								</p>
								<ul className="mt-3 space-y-2 text-xs">
									<li className="flex items-center gap-2">
										<span
											className={`w-2 h-2 rounded-full ${
												prepStep === "organize"
													? "border-2 border-yellow-300 border-t-transparent animate-spin"
													: ["distribute", "monitor-start", "monitor-sync", "done"].includes(prepStep)
														? "bg-emerald-400"
														: "bg-gray-500/70"
											}`}
										/>
										<span>Organizando camadas</span>
									</li>
									<li className="flex items-center gap-2">
										<span
											className={`w-2 h-2 rounded-full ${
												prepStep === "distribute"
													? "border-2 border-yellow-300 border-t-transparent animate-spin"
													: ["monitor-start", "monitor-sync", "done"].includes(prepStep)
														? "bg-emerald-400"
														: "bg-gray-500/70"
											}`}
										/>
										<span>Distribuindo páginas</span>
									</li>
									<li className="flex items-center gap-2">
										<span
											className={`w-2 h-2 rounded-full ${
												prepStep === "monitor-start"
													? "border-2 border-yellow-300 border-t-transparent animate-spin"
													: ["monitor-sync", "done"].includes(prepStep)
														? "bg-emerald-400"
														: "bg-gray-500/70"
											}`}
										/>
										<span>Iniciando monitoramento</span>
									</li>
									<li className="flex items-center gap-2">
										<span
											className={`w-2 h-2 rounded-full ${
												prepStep === "monitor-sync"
													? "border-2 border-yellow-300 border-t-transparent animate-spin"
													: prepStep === "done"
														? "bg-emerald-400"
														: "bg-gray-500/70"
											}`}
										/>
										<span>Sincronizando objetos</span>
									</li>
								</ul>
							</>
						)}
					</div>
				</div>
			)}
		</>
	);
}
